import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridColumnMenu } from "@mui/x-data-grid";
import { Button, Grid, Stack } from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";

import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";

import { formatUtilityAddress } from "utils/formatUtilityAddress";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { generateFormData, getImageUrl } from "utils";

import {
  useGetPendingTaxInvoiceApprovalForOtherUtilityQuery,
  usePutTaxInvoiceApprovalData,
} from "apis/DemandEstimation";

import { StyledGrid, StyledIconButton } from "./style";
import { DatePicker } from "@mui/x-date-pickers";


const CustomColumnMenu = (props) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuColumnsItem: null,
      columnMenuSortItem: null,
    }}
  />
);

const TaxInvoiceActionModal = ({ t, handleClose }) => {
  const {
    data: { data: pendingTaxInvoiceApprovalData } = {},
    isLoading: isTaxInvoiceLoading,
  } = useGetPendingTaxInvoiceApprovalForOtherUtilityQuery();

  const [approveTaxInvoice, { isLoading }] = usePutTaxInvoiceApprovalData();

  const [selectedTaxInvoice, setSelectedTaxInvoice] = useState([]);
  const [isApproveButtonClicked, setIsApproveButtonClicked] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [taxInvoiceDate,setTaxInvoiceDate]= useState(moment());
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const columns = useMemo(() => {
    return [
      {
        headerName: t("Application Number"),
        field: "applicationNumber",
        width: 150,
      },
      {
        headerName: t("Application Type"),
        field: "applicationType",
        width: 200,
        valueGetter: ({ row: { applicationType: { name } = {} } = {} }) => name,
      },
      {
        headerName: t("Name"),
        field: "name",
        width: 150,
        valueGetter: ({ row: { name } = {} }) => name,
      },
      {
        headerName: t("Utility Address"),
        field: "utilityAddress",
        width: 250,
        valueGetter: ({ row: { utilityAddress } = {} }) => {
          const address = formatUtilityAddress(utilityAddress);
          if (address) {
            return `${address}, Gandhinagar-382050`;
          }
          return "--";
        },
      },
      {
        headerName: t("Date"),
        field: "date",
        width: 150,
        valueGetter: ({ row: { submissionDate } = {} }) =>
          moment(submissionDate).isValid()
            ? convertUTCDateToLocalDate(submissionDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        headerName: t("Status"),
        field: "status",
        width: 200,
        valueGetter: ({ row: { applicationStatus: { name } = {} } }) => name,
      },
      {
        headerName: t("Action"),
        field: "action",
        width: 150,
        renderCell: ({ row: { taxInvoiceUrl } = {} }) => {
          return (
            <StyledIconButton
              onClick={() => {
                setDocumentToPreview({
                  url: getImageUrl(taxInvoiceUrl),
                  documentTypeName: "Tax Invoice",
                  documentType: "pdf",
                  documentName: "taxInvoice",
                });
              }}
            >
              <VisibilityIcon
                fontSize="small"
                color="primary"
                className="cursor-pointer"
              />
            </StyledIconButton>
          );
        },
      },
    ];
  }, []);

  const submitHandlerForApproval = async () => {
    try {
      const formData = new FormData();
      generateFormData(formData, {
        applicationIds: selectedTaxInvoice,
        isApproved: true,
        taxInvoiceDate:taxInvoiceDate?.format("YYYY-MM-DD")
      });
      await approveTaxInvoice(formData);
      setIsApproveButtonClicked(false);
      setSelectedTaxInvoice([]);
    } catch (err) {}
  };

  return (
    <>
      <Backdrop open={isTaxInvoiceLoading} />

      {isApproveButtonClicked && (
        <ConfirmationDialog
          message="Are you sure you want to approve selected tax invoice?"
          onCancel={() => setIsApproveButtonClicked(false)}
          onConfirm={submitHandlerForApproval}
          isOpen={isApproveButtonClicked}
          title="Warning"
          variant="warning"
          isLoading={isLoading}
        />
      )}

      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        title={t("Tax Invoice")}
        maxWidth="lg"
      >
      <Grid container columnSpacing={2} rowSpacing={1}>
        <Grid
            xs={12}
            sm={6}
            md={4}
            lg={3}
            className="mt-4 ms-3"
          >
            <DatePicker
              label={t("Start Date")}
              className="w-100 mb-2"
              format="DD-MM-YYYY"
              value={taxInvoiceDate ?? null}
              disableFuture
              onChange={(date) =>
                setTaxInvoiceDate(date)
              }
            />
          </Grid>
      </Grid>
        <StyledGrid
          className="mt-2"
          checkboxSelection
          autoHeight
          disableRowSelectionOnClick
          rows={pendingTaxInvoiceApprovalData || []}
          columns={columns}
          pageSizeOptions={[10, 50, 100]}
          rowCount={pendingTaxInvoiceApprovalData?.length}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          getRowId={(row) => row.id}
          onRowSelectionModelChange={(ids) => {
            // eslint-disable-next-line no-undef
            const selectedIDs = new Set(ids);
            setSelectedTaxInvoice(Array.from(selectedIDs));
          }}
          slots={{
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {t("No records found")}.
              </Stack>
            ),
          }}
          localeText={{
            footerRowSelected: () => {
              const selectedDemandEstimateIdsLength = selectedTaxInvoice.length;
              if (selectedDemandEstimateIdsLength) {
                return `${selectedDemandEstimateIdsLength} ${
                  selectedDemandEstimateIdsLength === 1 ? "row" : "rows"
                } selected`;
              }
            },
          }}
          rowSelection
          rowSelectionModel={selectedTaxInvoice}
          keepNonExistentRowsSelected
        />

        <div className="text-end">
          <Button
            variant="contained"
            className="mt-2"
            disabled={!selectedTaxInvoice.length}
            sx={{ mr: 2 }}
            color="success"
            onClick={() => setIsApproveButtonClicked(true)}
          >
            {t("Approve")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

TaxInvoiceActionModal.propTypes = {
  handleClose: PropTypes.func,
  isPowerUtility: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(TaxInvoiceActionModal);
